import React from 'react';
import { Trans } from '@lingui/react/macro';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import merge from 'lodash/merge';

import { useViewTransition } from '../hooks/useViewTransition';
import { Dialog, DialogProps } from './Dialog';

export type StepDialogStep = {
  children: React.ReactNode;
  dialogProps: Omit<DialogProps, 'onClose'>;
};

export type StepDialogProps = {
  currentStep: number;
  stepCount: number;
  onStepChange: (step: number) => void;
  defaultDialogProps: DialogProps;
  steps: Array<StepDialogStep>;
  disableStepChange?: boolean;
};

export default function StepDialog({
  currentStep,
  stepCount,
  defaultDialogProps,
  steps,
  onStepChange,
  disableStepChange,
}: StepDialogProps) {
  const theme = useTheme();
  const dialogProps = merge(
    {},
    defaultDialogProps,
    steps[currentStep - 1]?.dialogProps
  ) as unknown as DialogProps;

  const startViewTransition = useViewTransition();

  const handleStepClick = (step: number) => {
    if (startViewTransition) {
      startViewTransition(() => {
        onStepChange(step);
      });
    } else {
      onStepChange(step);
    }
  };

  return (
    <Dialog
      {...dialogProps}
      actions={
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="body2" sx={{ width: '100%' }}>
            <Trans context="UI library component for showing progress on a multi-step flow">
              Step {currentStep} of {stepCount}
            </Trans>
          </Typography>
          <Stack
            gap={1}
            direction="row"
            sx={{ width: '100%' }}
            justifyContent="center"
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                onClick={
                  disableStepChange
                    ? undefined
                    : () => handleStepClick(index + 1)
                }
                sx={{
                  width: index + 1 === currentStep ? '12px' : '6px',
                  height: '6px',
                  borderRadius: '8px',
                  background:
                    index + 1 === currentStep
                      ? theme.palette.cobalt50
                      : theme.palette.cobalt20,
                  cursor: disableStepChange ? 'default' : 'pointer',
                  transition:
                    'width 200ms ease-in-out, background 200ms ease-in-out',
                }}
              />
            ))}
          </Stack>
          <Stack
            direction="row"
            gap={0.5}
            sx={{ width: '100%' }}
            justifyContent="flex-end"
          >
            {dialogProps.actions}
          </Stack>
        </Stack>
      }
    >
      {steps[currentStep - 1]?.children}
    </Dialog>
  );
}
